import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { GetBrandsActions } from "../../actions/GetBrands";
import { SearchPlanDetailsAction } from "../../actions/SearchPlanDetailsAction";
import { validateField, validateFullForm } from "../../utils/validations";
import {
  saveGadgetDetails,
  sendGadgetPaymentRequest,
} from "../../actions/FormActions";
import INDIA_STATES_DATA from "../../constant/indiaStatesDistricts.json";

// Re-exports
export { PLANS, PLAN_META } from "../../constant/ProtectionPlanData";

// ── India states & districts derived from JSON ─────────────────────────────
const INDIA_STATES = INDIA_STATES_DATA.india_states_cities;

/** Returns sorted list of state names */
export const getStateList = () => INDIA_STATES.map((s) => s.state).sort();

/** Returns districts for a given state name */
export const getDistrictsByState = (stateName) => {
  const entry = INDIA_STATES.find((s) => s.state === stateName);
  return entry ? entry.districts.sort() : [];
};

// ── Constants ──────────────────────────────────────────────────────────────
export const DEVICE_TYPE_ICONS = {
  "Mobile Protection": "📱",
  Tablet: "🖥",
  Laptop: "💻",
};

export const PLATFORM_ICONS = {
  android: "🤖",
  ios: "🍎",
  windows: "🪟",
  macos: "�",
};

export const PLATFORM_LABELS = {
  android: "Android",
  ios: "iOS",
  windows: "Windows",
  macos: "macOS",
};

// ── Helpers ──────────────────────────────────────────────────────────────────
export const fmt = (n) =>
  n ? "₹" + Math.round(n).toLocaleString("en-IN") : "—";

const EMPTY_FORM = {
  fullName: "",
  email: "",
  mobile: "",
  zip: "",
  state: "",
  city: "",
  address: "",
  brand: "",
  imei: "",
  deviceValue: "",
};

/**
 * Parses range strings like "₹ 1 - ₹ 1,00,000" into { min, max }.
 */
export function parseRangeString(rangeStr) {
  if (!rangeStr?.trim()) return null;
  const numbers = rangeStr.match(/[\d,]+/g);
  if (!numbers || numbers.length < 2) return null;
  const toNum = (s) => Number(s.replace(/,/g, ""));
  return { min: toNum(numbers[0]), max: toNum(numbers[1]) };
}

/**
 * Returns true when variant uses dynamic % pricing (plan_Amount === 0).
 */
export function isPercentagePlan(variant) {
  return variant?.plan_Amount === 0;
}

/**
 * Returns true when variant defines a device value range.
 */
export function hasDeviceValueRange(variant) {
  return Boolean(variant?.range?.trim());
}

/**
 * Returns true when device value must be entered and validated.
 * Applies when plan uses % pricing or the variant has a range.
 */
export function requiresDeviceValueCheck(variant) {
  if (!variant) return false;
  return isPercentagePlan(variant) || hasDeviceValueRange(variant);
}

/**
 * Validates device value — range check and/or required for % plans.
 */
export function validateDeviceValueForVariant(value, variant) {
  if (!variant) return "";

  const isPercentage = isPercentagePlan(variant);
  const hasRange = hasDeviceValueRange(variant);

  if (!isPercentage && !hasRange) return "";

  const v = String(value ?? "");
  const num = Number(v);

  if (!v || num <= 0) {
    if (isPercentage) return "Enter device value to calculate premium.";
    return "Enter a valid device invoice value.";
  }

  if (hasRange) {
    const range = parseRangeString(variant.range);
    if (range && (num < range.min || num > range.max)) {
      return `Device value must be between ${fmt(range.min)} and ${fmt(range.max)}.`;
    }
  }

  return "";
}

// ── File → Base64 helper ───────────────────────────────────────────────────
/**
 * Converts a File or Blob to a base64-encoded string (without the data URI prefix).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is "data:<mime>;base64,<data>" — strip the prefix
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}
/**
 * Calculates premium for a selected variant.
 *
 * Rules:
 *   - plan_Amount > 0  → use plan_Amount directly (no percentage calc)
 *   - plan_Amount === 0 → base = deviceValue * (dynamicPricePercentage / 100)
 *
 * Returns { base, gst, total } all as numbers (0 when not calculable).
 */
export function calcVariantPremium(variant, deviceValue) {
  if (!variant) return { base: 0, gst: 0, total: 0 };

  let base = 0;
  if (variant.plan_Amount > 0) {
    base = variant.plan_Amount;
  } else if (variant.dynamicPricePercentage > 0 && deviceValue > 0) {
    base = (deviceValue * variant.dynamicPricePercentage) / 100;
  }

  const gst = base * 0.18;
  const total = base + gst;
  return { base, gst, total };
}

// ── useBrands hook ──────────────────────────────────────────────────────────
/**
 * Fetches device brand list from API on mount.
 * Falls back to FALLBACK_BRANDS if API is unreachable.
 *
 * Returns: { brands: Array<{id, value}|string>, loading, error }
 */
export function useBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchBrands = async () => {
      setLoading(true);
      setError(null);

      const result = await GetBrandsActions();
      if (cancelled) return;

      if (result.success && result.data.length > 0) {
        setBrands(result.data);
        // toast.success("Brands loaded from API!", { toastId: "brands-success" });
      } else {
        const errorMsg = result.error || "Could not load brands from server.";
        setError(errorMsg);
        toast.error(errorMsg, { toastId: "brands-error" });
        setBrands([]);
      }

      setLoading(false);
    };

    fetchBrands();
    return () => {
      cancelled = true;
    };
  }, []);

  return { brands, loading, error };
}

// ── usePlanData hook ────────────────────────────────────────────────────────
export function usePlanData() {
  const [searchParams] = useSearchParams();

  // Read planName from URL
  const urlPlanName = (
    searchParams.get("planName") ||
    searchParams.get("PlanName") ||
    ""
  ).trim();

  // Read varientId from URL
  const urlVariantId =
    searchParams.get("varientId") ||
    searchParams.get("variantId") ||
    searchParams.get("VariantId") ||
    searchParams.get("VarientId") ||
    null;

  const [plans, setPlans] = useState([]);
  const [autoSelectedPlan, setAutoSelectedPlan] = useState(null);
  const [selectedPlan, setSelectedPlanRaw] = useState(null);
  const [selectedType, setSelectedTypeRaw] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPlans = async () => {
      setLoading(true);
      setError(null);

      const result = await SearchPlanDetailsAction();
      if (cancelled) return;

      if (result?.success && result.plans.length > 0) {
        setPlans(result.plans);

        let matched = null;

        // 1. Match by planName from URL (case-insensitive)
        // Tries exact first, then startsWith — handles API appending " - XX months" to the name
        if (urlPlanName) {
          const needle = urlPlanName.toLowerCase();

          // exact match
          matched = result.plans.find(
            (p) => (p.planName || "").trim().toLowerCase() === needle,
          );

          // API planName starts with the URL value (e.g. "Cyber Digital... - 12 months" starts with "Cyber Digital...")
          if (!matched) {
            matched = result.plans.find((p) =>
              (p.planName || "").trim().toLowerCase().startsWith(needle),
            );
          }

          console.log("[usePlanData] urlPlanName:", urlPlanName);
          console.log(
            "[usePlanData] matched by planName:",
            matched?.planName || "none",
          );
        }

        // 2. Match by varientId from URL — find which plan owns that variant
        //    Run even if planName already matched, so we can pre-select type + variant
        if (urlVariantId) {
          for (const plan of result.plans) {
            const found = plan.variants.find(
              (v) => String(v.plan_VarriantsId) === String(urlVariantId),
            );
            if (found) {
              // only override banner plan if planName didn't already match
              if (!matched) matched = plan;
              // always pre-select the plan, type and variant
              setSelectedPlanRaw(plan);
              setAutoSelectedPlan(plan);
              setSelectedTypeRaw(found.type);
              setSelectedVariant(found);
              break;
            }
          }
        }

        // 3. Fallback — first plan
        if (!matched) matched = result.plans[0];

        // Only set banner + selected if variantId didn't already set them above
        if (!urlVariantId) {
          setAutoSelectedPlan(matched);
          setSelectedPlanRaw(matched);
        }

        toast.success(`Plan loaded: ${matched.planName}`, {
          toastId: "plan-success",
        });
      } else {
        const errorMsg = result?.error || "Failed to fetch plan details.";
        setError(errorMsg);
        setPlans([]);
        toast.error(errorMsg, { toastId: "plan-error" });
      }

      setLoading(false);
    };

    fetchPlans();
    return () => {
      cancelled = true;
    };
  }, [urlPlanName, urlVariantId]);

  const setSelectedPlan = (plan) => {
    setSelectedPlanRaw(plan);
    setSelectedTypeRaw(null);
    setSelectedVariant(null);
  };

  const setSelectedType = (type) => {
    setSelectedTypeRaw(type);
    setSelectedVariant(null);
  };

  const deviceTypes = selectedPlan
    ? [...new Set(selectedPlan.variants.map((v) => v.type))]
    : [];

  const platforms =
    selectedPlan && selectedType
      ? selectedPlan.variants.filter((v) => v.type === selectedType)
      : [];

  return {
    plans,
    autoSelectedPlan,
    selectedPlan,
    setSelectedPlan,
    selectedType,
    setSelectedType,
    selectedVariant,
    setSelectedVariant,
    deviceTypes,
    platforms,
    loading,
    error,
  };
}

// ── useProtectForm hook ───────────────────────────────────────────────────────
/**
 * Main form hook — composes brands, plan data, form state, validation & submit.
 */
export function useProtectForm() {
  const { brands, loading: brandsLoading } = useBrands();
  const {
    plans,
    autoSelectedPlan,
    selectedPlan,
    setSelectedPlan,
    selectedType,
    setSelectedType,
    selectedVariant,
    setSelectedVariant,
    deviceTypes,
    platforms,
    loading: plansLoading,
    error: plansError,
  } = usePlanData();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const deviceValue = parseFloat(form.deviceValue) || 0;
  const premium = calcVariantPremium(selectedVariant, deviceValue);

  const selForValidation = useMemo(
    () => ({
      os: selectedVariant?.platform || null,
      dev: selectedVariant?.type || null,
      plan: selectedPlan?.planName || null,
      benefit: selectedVariant || null,
    }),
    [selectedVariant, selectedPlan],
  );

  const isFormValid = useMemo(() => {
    const fields = [
      "fullName",
      "email",
      "mobile",
      "zip",
      "brand",
      "imei",
      "state",
      "city",
    ];
    if (fields.some((f) => validateField(f, form[f]) !== "")) return false;
    if (requiresDeviceValueCheck(selectedVariant)) {
      if (validateDeviceValueForVariant(form.deviceValue, selectedVariant))
        return false;
    }
    if (!selectedVariant) return false;
    if (!invoiceFile) return false;
    if (!recordingBlob) return false;
    if (!termsChecked) return false;
    return true;
  }, [form, selectedVariant, invoiceFile, recordingBlob, termsChecked]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Mobile: digits only, max 10
    if (name === "mobile") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setForm((f) => ({ ...f, mobile: digits }));
      setErrors((prev) => ({
        ...prev,
        mobile: validateField("mobile", digits),
      }));
      return;
    }

    // PIN code: digits only, max 6
    if (name === "zip") {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      setForm((f) => ({ ...f, zip: digits }));
      setErrors((prev) => ({ ...prev, zip: validateField("zip", digits) }));
      return;
    }

    // State change → clear city
    if (name === "state") {
      setForm((f) => ({ ...f, state: value, city: "" }));
      setErrors((prev) => ({
        ...prev,
        state: validateField("state", value),
        city: "",
      }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    if (name === "deviceValue") {
      setErrors((prev) => ({
        ...prev,
        deviceValue: validateDeviceValueForVariant(value, selectedVariant),
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleInvoice = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setInvoiceFile(file);
      setErrors((prev) => ({ ...prev, invoice: "" }));
    }
  };

  const handleTerms = (e) => {
    setTermsChecked(e.target.checked);
    setErrors((prev) => ({
      ...prev,
      terms: e.target.checked ? "" : prev.terms,
    }));
  };

  const handleRecordingSave = (blob) => {
    setRecordingBlob(blob);
    setErrors((prev) => ({ ...prev, recording: "" }));
  };

  const handleActivate = async () => {
    // Validate form
    const errs = validateFullForm(
      form,
      selForValidation,
      invoiceFile,
      recordingBlob,
      termsChecked,
    );

    // Replace generic deviceValue check with variant-aware validation
    delete errs.deviceValue;
    if (requiresDeviceValueCheck(selectedVariant)) {
      const deviceValueErr = validateDeviceValueForVariant(
        form.deviceValue,
        selectedVariant,
      );
      if (deviceValueErr) errs.deviceValue = deviceValueErr;
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      document
        .querySelector(".form-group--error, .notice-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    // Convert files to base64 in parallel
    let mobileImgBase64 = "";
    let uploadDocumentBase64 = "";

    try {
      if (recordingBlob) {
        mobileImgBase64 = await fileToBase64(recordingBlob);
      }
      if (invoiceFile) {
        uploadDocumentBase64 = await fileToBase64(invoiceFile);
      }
    } catch {
      const errorMsg = "Failed to process uploaded files. Please try again.";
      setSubmitError(errorMsg);
      setIsSubmitting(false);
      toast.error(errorMsg, { toastId: "file-error" });
      return;
    }

    // Build API payload
    const apiPayload = {
      name: form.fullName.trim(),
      email: form.email.trim(),
      mobileNumber: form.mobile.trim(),
      imei: form.imei.trim(),
      brand: form.brand,
      model: selectedVariant?.type || "",
      planVarriantId: selectedVariant?.plan_VarriantsId || 0,
      mobileImg: mobileImgBase64,
      pincode: form.zip.trim(),
      // address: form.address.trim(),
      city: form.city,
      state: form.state,
      country: "India",
      planName: selectedPlan?.planName || "",
      planAmount: selectedVariant?.planAmount || 0,
      // planId: selectedVariant?.planId, TODO: commented for the wrong planId
      createdDate: new Date().toISOString(),
      url: window.location.origin,
      uploadDocument: uploadDocumentBase64,
      extension: invoiceFile ? `.${invoiceFile.name.split(".").pop()}` : "",
      deviceAmt: parseFloat(form.deviceValue) || 0,
      amount: premium.total,
      gst: premium.gst,
      premiumAmount: premium.total,
      dynamicPricePercentage: selectedVariant?.dynamicPricePercentage || 0,
    };

    // console.log("📦 Step 1 - Saving gadget details:", apiPayload);

    // Step 1: Save gadget details
    const step1Result = await saveGadgetDetails(apiPayload);

    if (!step1Result.success) {
      const errorMsg = step1Result.error || "Failed to save gadget details.";
      setSubmitError(errorMsg);
      setIsSubmitting(false);
      toast.error(errorMsg, { toastId: "submit-error" });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // toast.success(step1Result.message || "Details saved successfully!", {
    //   toastId: "step1-success",
    // });

    // console.log(`✅ Step 1 Complete - Customer ID: ${step1Result.customerId}`);

    // Step 2: Send payment request with customerId as userId
    const paymentPayload = {
      ...apiPayload,
      userId: String(step1Result.customerId),
    };

    // console.log("📦 Step 2 - Initiating payment:", paymentPayload);

    const step2Result = await sendGadgetPaymentRequest(paymentPayload);

    setIsSubmitting(false);

    if (step2Result.success) {
      // toast.success(
      //   "saved successfully! and Redirecting to payment gateway...",
      //   {
      //     toastId: "payment-redirect",
      //   },
      // );

      // console.log(`🔗 Redirecting to: ${step2Result.paymentUrl}`);

      // Redirect to payment URL
      setTimeout(() => {
        window.location.href = step2Result.paymentUrl;
      }, 1000);
    } else {
      const errorMsg = step2Result.error || "Failed to initiate payment.";
      setSubmitError(errorMsg);
      toast.error(errorMsg, { toastId: "submit-error" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resetForm = () => {
    setSubmitSuccess(false);
    setForm(EMPTY_FORM);
    setInvoiceFile(null);
    setRecordingBlob(null);
    setTermsChecked(false);
    setErrors({});
  };

  // Re-validate device value when variant changes
  useEffect(() => {
    if (form.deviceValue) {
      setErrors((prev) => ({
        ...prev,
        deviceValue: validateDeviceValueForVariant(
          form.deviceValue,
          selectedVariant,
        ),
      }));
    }
  }, [selectedVariant]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    brands,
    brandsLoading,
    plans,
    autoSelectedPlan,
    selectedPlan,
    setSelectedPlan,
    selectedType,
    setSelectedType,
    selectedVariant,
    setSelectedVariant,
    deviceTypes,
    platforms,
    plansLoading,
    plansError,
    form,
    errors,
    invoiceFile,
    recordingBlob,
    cameraOpen,
    setCameraOpen,
    termsChecked,
    isSubmitting,
    submitSuccess,
    submitError,
    deviceValue,
    premium,
    isFormValid,
    handleChange,
    handleInvoice,
    handleTerms,
    handleRecordingSave,
    handleActivate,
    resetForm,
    // State & city helpers
    stateList: getStateList(),
    cityList: getDistrictsByState(form.state),
  };
}
